import { keepPreviousData, useQuery } from '@tanstack/react-query';

import axiosInstance from '../lib/axios';
import type { AiLogDetail, AiLogFilters, AiLogList, AiLogSummary } from '../types/aiLog';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const aiLogKeys = {
  all: ['admin', 'ai-logs'] as const,
  list: (page: number, pageSize: number, filters: AiLogFilters) =>
    [...aiLogKeys.all, 'list', page, pageSize, filters] as const,
  summary: (filters: AiLogFilters) => [...aiLogKeys.all, 'summary', filters] as const,
  detail: (publicId: string | null) => [...aiLogKeys.all, 'detail', publicId] as const,
};

const toIsoString = (value: Date | string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const compactFilters = (filters: AiLogFilters) => {
  return {
    from: toIsoString(filters.from),
    to: toIsoString(filters.to),
    userId: filters.userId?.trim() || undefined,
    email: filters.email?.trim() || undefined,
    origin: filters.origin || undefined,
    model: filters.model?.trim() || undefined,
    capabilityCode: filters.capabilityCode?.trim() || undefined,
    status: filters.status || undefined,
    apiKeyId: filters.apiKeyId?.trim() || undefined,
    variance: filters.variance || undefined,
  };
};

const fetchAiLogs = async (
  page: number,
  pageSize: number,
  filters: AiLogFilters
): Promise<AiLogList> => {
  const response = await axiosInstance.get<ApiResponse<AiLogList>>('/v1/admin/ai-logs', {
    params: {
      ...compactFilters(filters),
      page,
      pageSize,
    },
  });

  if (!response.data.data) {
    throw new Error(response.data.message || 'AI usage logs response is empty');
  }

  return response.data.data;
};

const fetchAiLogSummary = async (filters: AiLogFilters): Promise<AiLogSummary> => {
  const response = await axiosInstance.get<ApiResponse<AiLogSummary>>('/v1/admin/ai-logs/summary', {
    params: compactFilters(filters),
  });

  if (!response.data.data) {
    throw new Error(response.data.message || 'AI usage summary response is empty');
  }

  return response.data.data;
};

const fetchAiLogDetail = async (publicId: string): Promise<AiLogDetail> => {
  const response = await axiosInstance.get<ApiResponse<AiLogDetail>>(
    `/v1/admin/ai-logs/${publicId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'AI usage log detail response is empty');
  }

  return response.data.data;
};

export const useAiLogs = (page: number, pageSize: number, filters: AiLogFilters) => {
  return useQuery({
    queryKey: aiLogKeys.list(page, pageSize, filters),
    queryFn: () => fetchAiLogs(page, pageSize, filters),
    placeholderData: keepPreviousData,
  });
};

export const useAiLogSummary = (filters: AiLogFilters) => {
  return useQuery({
    queryKey: aiLogKeys.summary(filters),
    queryFn: () => fetchAiLogSummary(filters),
  });
};

export const useAiLogDetail = (publicId: string | null) => {
  return useQuery({
    queryKey: aiLogKeys.detail(publicId),
    queryFn: () => fetchAiLogDetail(publicId ?? ''),
    enabled: Boolean(publicId),
  });
};
